import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from "react-router-dom";
import axios from 'axios';
import SftGlobalData from '../../library/SftGlobalData';
import Colors from '../../library/Colors';
import Button from '@mui/material/Button';
import PropTypes from 'prop-types';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import { faPencilAlt, faPlay  } from '@fortawesome/free-solid-svg-icons';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import TablePagination from '@mui/material/TablePagination';
import TableSortLabel from '@mui/material/TableSortLabel';
import { visuallyHidden } from '@mui/utils';
import { FormControlLabel, Switch } from '@mui/material';
import { DataGridPro, useGridApiContext } from '@mui/x-data-grid-pro';

import ListItemV2 from '../../components/ListItemV2';
import FloatingAddButton from '../../components/FloatingAddButton';

function CustomEditComponent(props) {
  const { id, value, field } = props;
  const apiRef = useGridApiContext();

  const handleValueChange = (event) => {
    const newValue = event.target.value; 

    apiRef.current.setEditCellValue({ id, field, value: newValue });
  };

  const saveValueChange = () => {
      SftGlobalData.post('mailIntegration&action=updateDescription', { key: props.sftapi, id: id, description: value }).then(response => {
          if(response.success){
              props.notify(props.t('savedChanges'), 7500, "top-right", props.theme, 1);
          }
      }).catch(e => {
          console.error(e);
      });
  }

  const handleEnter = (e) => {
    if(e.keyCode === 13){
        saveValueChange();
    }
  }

  return <input type="text" value={value} onChange={handleValueChange} onKeyDown={(event)=>{handleEnter(event);}} onBlur={()=>{saveValueChange()}}/>;
}

function EnhancedTableHead(props) {
    const {  order, orderBy, onRequestSort } =
      props;
    const createSortHandler = (property) => (event) => {
      onRequestSort(event, property);
    };
    return (
      <TableHead>
        <TableRow className={props.theme}>
          {headCells.map((headCell) => (
            <TableCell
              className={props.theme}
              key={headCell.id}
              align={headCell.numeric ? 'right' : 'left'}
              padding={headCell.disablePadding ? 'none' : 'normal'}
              sortDirection={orderBy === headCell.id ? order : false}
              // Widths are what make tableLayout: 'fixed' usable — see the Table below. The last
              // column deliberately has none so it absorbs whatever width is left over.
              sx={{ width: headCell.width }}
            >
              <TableSortLabel
                className={props.theme + " bold"}
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
    );
  }

  const headCells = [
    {
        id: 'expand',
        // Fits the small IconButton (34px) plus the cell's default 16px padding on each side.
        width: 72,
        numeric: false,
        disablePadding: false,
        label: '',
    },
    {
      id: 'sTimeDate',
      width: 140,
      numeric: false,
      disablePadding: false,
      label: 'Date',
    },
    {
      id: 'sTimeStart',
      width: 120,
      numeric: false,
      disablePadding: false,
      label: 'Time start',
    },
    {
      id: 'sTimeEnd',
      width: 120,
      numeric: false,
      disablePadding: false,
      label: 'Time end',
    },
    {
      id: 'sStatus',
      width: 120,
      numeric: true,
      disablePadding: false,
      label: 'Status',
    },
    {
      id: 'sMailsAdded',
      width: 100,
      numeric: true,
      disablePadding: false,
      label: 'Added',
    },
    {
      id: 'sMailsUpdated',
      width: 100,
      numeric: true,
      disablePadding: false,
      label: 'Updated',
    },
    {
      id: 'sMailsErrors',
      numeric: true,
      disablePadding: false,
      label: 'Errors',
    },
  ];

function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }
  
  function getComparator(order, orderBy) {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }
  
  // This method is created for cross-browser compatibility, if you don't
  // need to support IE11, you can use Array.prototype.sort() directly
  function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }

// Identity columns: pinned left and autosized to fit their content (see the grid's
// pinnedColumns / autosizeOptions). Everything else is a pushed data field.
const DETAIL_PINNED = ['custno', 'email', 'source'];

// The identity columns actually present in the given rows, in pin order. Drives both the
// left-pinning and which columns get autosized so only these are fit-to-content.
function detailPinnedColumns(rows) {
  const keys = new Set();
  (rows || []).forEach((r) => Object.keys(r || {}).forEach((k) => keys.add(k)));
  return DETAIL_PINNED.filter((k) => keys.has(k));
}

// Build DataGridPro columns dynamically from the union of keys present in the rows. custno/email/source
// are pinned first; the rest (the data we push to the provider — FNAME, LNAME, properties) follow.
function buildDynamicColumns(rows) {
  const keys = new Set();
  (rows || []).forEach((r) => Object.keys(r || {}).forEach((k) => keys.add(k)));
  const preferred = DETAIL_PINNED.filter((k) => keys.has(k));
  const rest = [...keys].filter((k) => !preferred.includes(k)).sort();
  return [...preferred, ...rest].map((k) => {
    const col = { field: k, headerName: k, display: 'flex' };
    if (preferred.includes(k)) {
      // Pinned identity column — autosized on mount to fit content; minWidth keeps it from
      // collapsing when a value is empty.
      col.minWidth = 80;
    } else {
      // Pushed data field (FNAME, LNAME, properties, …): narrow to keep the row compact, but
      // resizable — drag the header edge to expand any field you want to read.
      col.width = 90;
      col.resizable = true;
    }
    return col;
  });
}

// Bounded height for a detail grid: fits up to ~8 rows then scrolls inside its own
// compact box. This keeps the horizontal scrollbar reachable at the bottom of the box
// (instead of at the bottom of a very tall autoHeight grid, which is awkward to reach
// when a record has many fields and the columns run off-screen to the right).
const DETAIL_ROW_H = 36;
function detailGridHeight(count, paginated) {
  const visibleRows = Math.min(count, 8);
  return visibleRows * DETAIL_ROW_H + 40 /* header */ + 16 /* h-scrollbar */ + (paginated ? 53 /* footer */ : 0);
}

// Collapsible wrapper for a run-detail section (Added / Updated / Wrong / Skipped). Each manages
// its own open state; collapsed by default to keep the drill-down compact for large lists.
function CollapsibleSection(props) {
  const { title, subtitle, theme, defaultOpen = false, children } = props;
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <Box sx={{ mt: 2 }}>
      <Box
        onClick={() => setOpen(!open)}
        sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}
      >
        <IconButton size="small" aria-label="expand section" onClick={(e) => { e.stopPropagation(); setOpen(!open); }}>
          {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
        <Typography className={theme} variant="h6" component="div">{title}</Typography>
      </Box>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box sx={{ pt: 1 }}>
          {subtitle && (
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>{subtitle}</Typography>
          )}
          {children}
        </Box>
      </Collapse>
    </Box>
  );
}

CollapsibleSection.propTypes = {
  title: PropTypes.node,
  subtitle: PropTypes.node,
  theme: PropTypes.string,
  defaultOpen: PropTypes.bool,
  children: PropTypes.node,
};

function Row(props) {
    const { row } = props;
    const [open, setOpen] = React.useState(false);
    const [detail, setDetail] = React.useState(undefined); // undefined=not fetched, null=none, object=loaded
    const [loadingDetail, setLoadingDetail] = React.useState(false);

    // run_details is no longer shipped with the sync-log list (it can be large); fetch it per run on
    // first expand. Errors (error_information) are still inline, so that grid renders without this call.
    const handleToggle = () => {
      const next = !open;
      setOpen(next);
      if (next && detail === undefined && !loadingDetail) {
        setLoadingDetail(true);
        SftGlobalData.post('mailIntegration&action=getSyncRunDetail', {
          key: props.api,
          date: row.date,
          start_time: row.start_time,
          end_time: row.end_time,
        }).then((response) => {
          const rd = response?.success ? (response.data?.run_details ?? null) : null;
          setDetail(parseRunDetails(rd));
        }).catch(() => {
          setDetail(null);
        }).finally(() => {
          setLoadingDetail(false);
        });
      }
    };

    const parseErrorsToRows = (errorStr) => {
      try {
        let errorObj = JSON?.parse(errorStr);
        let rows = [];
        let id = 0;

        Object.keys(errorObj).forEach((key) => {
          const items = errorObj[key];
          if (Array.isArray(items)) {
            items.forEach((item) => {
              if (key === 'bulkImportErrors' && item.errors) {
                try {
                  const batchErrors = JSON.parse(item.errors);
                  batchErrors.forEach((error) => {
                    rows.push({
                      id: id++,
                      category: key,
                      batch: item.batch || '',
                      status: error?.status || '',
                      title: error?.title || '',
                      detail: error?.detail || '',
                      pointer: error?.source?.pointer || ''
                    });
                  });
                } catch (e) {
                  rows.push({ id: id++, category: key, batch: item.batch || '', status: '', title: '', detail: JSON.stringify(item), pointer: '' });
                }
              } else if (typeof item === 'object') {
                rows.push({ id: id++, category: key, batch: '', status: item?.status || '', title: item?.title || '', detail: item?.detail || JSON.stringify(item), pointer: item?.source?.pointer || '' });
              } else {
                rows.push({ id: id++, category: key, batch: '', status: '', title: '', detail: String(item), pointer: '' });
              }
            });
          } else if (items && typeof items === 'string') {
            rows.push({ id: id++, category: key, batch: '', status: '', title: '', detail: items, pointer: '' });
          }
        });

        return rows;
      } catch (e) {
        return [];
      }
    }

    // Parse the separate run_details column (per-customer added / wrong). Returns null when
    // absent (old rows, or a provider that didn't populate it) so nothing extra is rendered.
    const parseRunDetails = (str) => {
      if (!str) return null;
      try {
        const obj = typeof str === 'string' ? JSON.parse(str) : str;
        if (!obj || typeof obj !== 'object') return null;
        const added = Array.isArray(obj.added) ? obj.added : [];
        const updated = Array.isArray(obj.updated) ? obj.updated : [];
        const wrongAll = Array.isArray(obj.wrong) ? obj.wrong : [];
        // Split intentional skips (benign — e.g. the shared-profile guard) out of real problems
        // so they don't read as errors.
        const wrong = wrongAll.filter((w) => w && w.type !== 'skipped');
        const skipped = wrongAll.filter((w) => w && w.type === 'skipped');
        if (added.length === 0 && updated.length === 0 && wrong.length === 0 && skipped.length === 0) return null;
        return {
          added,
          updated,
          wrong,
          skipped,
          addedCount: obj.addedCount ?? added.length,
          addedTruncated: !!obj.addedTruncated,
          updatedCount: obj.updatedCount ?? updated.length,
          updatedTruncated: !!obj.updatedTruncated,
        };
      } catch (e) {
        return null;
      }
    }

    const errorRows = parseErrorsToRows(row.error_information);

    return (
      <React.Fragment>
        <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
          <TableCell>
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={handleToggle}
            >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </TableCell>
          <TableCell className={props.theme} align="left">{row.date}</TableCell>
          <TableCell className={props.theme} align="left">{row.start_time}</TableCell>
          <TableCell className={props.theme} align="left">{row.end_time}</TableCell>
          <TableCell className={props.theme} align="right">{row.status}</TableCell>
          <TableCell className={props.theme} align="right">{row.added}</TableCell>
          <TableCell className={props.theme} align="right">{row.updated}</TableCell>
          <TableCell className={props.theme} align="right">{row.error}</TableCell>
        </TableRow>
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              {/* minWidth: 0 lets this box shrink to the (now fixed) cell width instead of being
                  sized by its widest grid; overflowX keeps that grid reachable inside the row. */}
              <Box sx={{ margin: 1, minWidth: 0, overflowX: 'auto' }}>
                <Typography className={props.theme} variant="h6" gutterBottom component="div">
                  Errors
                </Typography>
                {errorRows.length > 0 ? (
                  <DataGridPro showToolbar
                    className={props.theme}
                    density="compact"
                    columns={[
                      { field: 'category', headerName: 'Category', width: 180, display: 'flex' },
                      { field: 'detail', headerName: 'Detail', flex: 1, minWidth: 400, display: 'flex' },
                    ]}
                    rows={errorRows}
                    sx={{ height: detailGridHeight(errorRows.length, errorRows.length > 10) }}
                    hideFooter={errorRows.length <= 10}
                    pagination={errorRows.length > 10}
                    rowHeight={36}
                  />
                ) : (
                  <Typography variant="body2" color="textSecondary">No error details available</Typography>
                )}

                {/* Per-customer run detail, lazy-fetched on expand from the separate run_details column.
                    Additive: the Errors grid above is untouched, so the Mailchimp display is unaffected. */}
                {loadingDetail && (
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>Loading run details…</Typography>
                )}
                {(() => {
                  if (!detail) return null;
                  return (
                    <React.Fragment>
                      {detail.added.length > 0 && (
                        <CollapsibleSection
                          theme={props.theme}
                          title={`Added${detail.addedTruncated ? ` (showing first ${detail.added.length} of ${detail.addedCount})` : ` (${detail.addedCount})`}`}
                        >
                          <DataGridPro showToolbar
                            className={props.theme}
                            density="compact"
                            columns={buildDynamicColumns(detail.added)}
                            rows={detail.added.map((r, i) => ({ id: i, ...r }))}
                            sx={{ height: detailGridHeight(detail.added.length, detail.added.length > 10) }}
                            pinnedColumns={{ left: detailPinnedColumns(detail.added) }}
                            autosizeOnMount
                            autosizeOptions={{ columns: detailPinnedColumns(detail.added), includeHeaders: true, includeOutliers: true }}
                            hideFooter={detail.added.length <= 10}
                            pagination={detail.added.length > 10}
                            rowHeight={36}
                          />
                        </CollapsibleSection>
                      )}
                      {detail.updated.length > 0 && (
                        <CollapsibleSection
                          theme={props.theme}
                          title={`Updated${detail.updatedTruncated ? ` (showing first ${detail.updated.length} of ${detail.updatedCount})` : ` (${detail.updatedCount})`}`}
                        >
                          <DataGridPro showToolbar
                            className={props.theme}
                            density="compact"
                            columns={buildDynamicColumns(detail.updated)}
                            rows={detail.updated.map((r, i) => ({ id: i, ...r }))}
                            sx={{ height: detailGridHeight(detail.updated.length, detail.updated.length > 10) }}
                            pinnedColumns={{ left: detailPinnedColumns(detail.updated) }}
                            autosizeOnMount
                            autosizeOptions={{ columns: detailPinnedColumns(detail.updated), includeHeaders: true, includeOutliers: true }}
                            hideFooter={detail.updated.length <= 10}
                            pagination={detail.updated.length > 10}
                            rowHeight={36}
                          />
                        </CollapsibleSection>
                      )}
                      {detail.wrong.length > 0 && (
                        <CollapsibleSection theme={props.theme} title={`Wrong (${detail.wrong.length})`}>
                          <DataGridPro showToolbar
                            className={props.theme}
                            density="compact"
                            columns={[
                              { field: 'type', headerName: 'Type', width: 150, display: 'flex' },
                              { field: 'custno', headerName: 'Customer', width: 110, display: 'flex' },
                              { field: 'email', headerName: 'Email', width: 220, display: 'flex' },
                              { field: 'field', headerName: 'Field', width: 160, display: 'flex' },
                              { field: 'reason', headerName: 'Reason', flex: 1, minWidth: 240, display: 'flex' },
                            ]}
                            rows={detail.wrong.map((r, i) => ({ id: i, ...r }))}
                            sx={{ height: detailGridHeight(detail.wrong.length, detail.wrong.length > 10) }}
                            hideFooter={detail.wrong.length <= 10}
                            pagination={detail.wrong.length > 10}
                            rowHeight={36}
                          />
                        </CollapsibleSection>
                      )}
                      {detail.skipped.length > 0 && (
                        <CollapsibleSection
                          theme={props.theme}
                          title={`Skipped (${detail.skipped.length})`}
                          subtitle="Bewust overgeslagen — geen fouten (bv. het e-mailadres hoort bij een klant die in deze run actief is)."
                        >
                          <DataGridPro showToolbar
                            className={props.theme}
                            density="compact"
                            columns={[
                              { field: 'custno', headerName: 'Customer', width: 110, display: 'flex' },
                              { field: 'email', headerName: 'Email', width: 240, display: 'flex' },
                              { field: 'reason', headerName: 'Reason', flex: 1, minWidth: 280, display: 'flex' },
                            ]}
                            rows={detail.skipped.map((r, i) => ({ id: i, ...r }))}
                            sx={{ height: detailGridHeight(detail.skipped.length, detail.skipped.length > 10) }}
                            hideFooter={detail.skipped.length <= 10}
                            pagination={detail.skipped.length > 10}
                            rowHeight={36}
                          />
                        </CollapsibleSection>
                      )}
                    </React.Fragment>
                  );
                })()}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  }
  
  Row.propTypes = {
    row: PropTypes.shape({
      date: PropTypes.string,
      start_time: PropTypes.string,
      end_time: PropTypes.string,
      status: PropTypes.string,
      added: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      updated: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      error: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      error_information: PropTypes.string,
      run_details: PropTypes.string,
    }).isRequired,
  };

  function RowWebhook(props) {
    const { row } = props;
  
    return (
      <React.Fragment>
        <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
          <TableCell className={props.theme} align="left">{SftGlobalData.dateTimeFormatter(row.rTime)}</TableCell>
          <TableCell className={props.theme} align="left">{row.rType}</TableCell>
          <TableCell className={props.theme} align="left">{row.rListID}</TableCell>
          <TableCell className={props.theme} align="left">{row.rSubID}</TableCell>
          <TableCell className={props.theme} align="left">{row.rEmail}</TableCell>
          <TableCell className={props.theme} align="left">{row.rFree1}</TableCell>
          <TableCell className={props.theme} align="left">{row.rFree2}</TableCell>
        </TableRow>
      </React.Fragment>
    );
  }
  
  RowWebhook.propTypes = {
    row: PropTypes.shape({
      rID: PropTypes.string.isRequired,
      rTime: PropTypes.string.isRequired,
      rType: PropTypes.string.isRequired,
      rListID: PropTypes.string.isRequired,
      rSubID: PropTypes.string.isRequired,
      rEmail: PropTypes.string.isRequired,
      rFree1: PropTypes.string.isRequired,
      rFree2: PropTypes.string.isRequired,
    }).isRequired,
  };


  const gridWebhookColumns = [
    {
        field: 'rTime',
        headerName: 'Time',
        width: 180,
        display: 'flex',
    },
    {
        field: 'rType',
        headerName: 'Type',
        width: 100,
        display: 'flex',
    },
    {
        field: 'rListID',
        headerName: 'List ID',
        width: 110,
        display: 'flex',
    },
    {
        field: 'rSubID',
        headerName: 'Sub ID',
        width: 110,
        display: 'flex',
    },
    {
        field: 'rEmail',
        headerName: 'Email',
        width: 280,
        display: 'flex',
    },
    {
        field: 'rFree1',
        headerName: 'Free 1',
        width: 130,
        display: 'flex',
    },
    {
        field: 'rFree2',
        headerName: 'Free 2',
        width: 100,
        display: 'flex',
    },
  ];


  

function TabPanel(props) {
    const { children, value, index, ...other } = props;
  
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
      >
        {value === index && (
          <Box sx={{ div: 3 }}>
            <div style={{marginTop: 10}}>{children}</div>
          </Box>
        )}
      </div>
    );
  }
  
  TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
  };
  
  function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
  }

const MailIntegrationsScreen = (props) => {
    const [loading, setLoading] = useState(false);
    const [lists, setLists] = useState([]);
    const [value, setValue] = useState(0);
    const [logRows, setLogRows] = useState([]);
    const [logRowsWebhook, setLogRowsWebhook] = useState([]);
    const [order, setOrder] = useState('desc');
    const [orderBy, setOrderBy] = useState('date');
    const [page, setPage] = useState(0);
    const [dense, setDense] = useState(true);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const gridIntegrationColumns = [
        {
            field: 'id',
            headerName: "ID",
            width: 50,
            align: "left",
            display: 'flex',
        },
        {
            field: 'type',
            headerName: "Type",
            headerClassName: 'bold',
            minWidth: 100,
            width: 150,
            align: "left",
            display: 'flex',
        },
        {
            field: 'description',
            headerName: props.t('mail_desc'),
            headerClassName: 'bold',
            minWidth: 200,
            width: 300,
            align: "left",
            display: 'flex',
            editable: true,
            renderEditCell: (params)=>{return <CustomEditComponent field={"description"} sftapi={props.api} {...params}/>}
        },
        {
            field: 'list_id',
            headerName: props.t('mail_list_id'),
            headerClassName: 'bold',
            minWidth: 100,
            width: 175,
            align: "left",
            display: 'flex',
        },
        {
            field: 'last_sync_started',
            headerName: props.t('mail_last_sync_started'),
            headerClassName: 'bold',
            minWidth: 100,
            width: 200,
            align: "left",
            display: 'flex',
        },
        {
            field: 'last_sync_end',
            headerName: props.t('mail_last_sync_end'),
            headerClassName: 'bold',
            minWidth: 100,
            width: 200,
            align: "left",
            display: 'flex',
        },
        {
            field: 'active',
            headerName: props.t('active'),
            width: 125,
            align: "left",
            display: 'flex',
            renderCell: (params)=>{return <ListItemV2 
                key={params.row.id} 
                id={params.row.id} 
                lang={props.lang} 
                name={params.row.description}
                config={params.row.sms_birthday_body} 
                active={params.row.active ? 'Y' : 'N'} 
                api={props.api} 
                theme={props.theme}
                submitHandler={(list,value)=>{changeMailCredentials(list,value)}}
                />}
        },
        {
            field: 'edit',
            headerName: props.t('edit'),
            minWidth: 80,    
            maxWidth: 80,  
            align: 'center',      
            headerAlign: 'center', 
            display: 'flex',
            renderCell: (params) => {
            return  <Button style={{color: (props.theme === 'dark' ? Colors.dark_text : Colors.light_text), height:"100%", borderRadius: "50%" }} onClick={()=>{navigate("/"+props.lang+"/mail-integrations/"+params.row.id)}} >
                        <FontAwesomeIcon icon={faPencilAlt} color={Colors.def_orange} size="2x" />
                    </Button>;        
            }
        },
        {
            field: 'daily',
            headerName: props.t('mail_run_daily'),
            minWidth: 80,    
            maxWidth: 80,  
            align: 'center',      
            headerAlign: 'center', 
            display: 'flex',
            renderCell: (params) => {
                if(params.row.active){
                  return  <Button style={{color: (props.theme === 'dark' ? Colors.dark_text : Colors.light_text), height:"100%", borderRadius: "50%" }} onClick={()=>{startMailSync(params.row.id,"daily")}} >
                            <FontAwesomeIcon icon={faPlay} color={Colors.def_green} size="2x" />
                          </Button>;       
                }else{
                  return <div></div>;
                }
              }
        },
        {
            field: 'full',
            headerName: props.t('mail_run_full'),
            minWidth: 80,    
            maxWidth: 80,  
            align: 'center',      
            headerAlign: 'center', 
            display: 'flex',
            renderCell: (params) => {
                if(params.row.active){
                  return  <Button style={{color: (props.theme === 'dark' ? Colors.dark_text : Colors.light_text), height:"100%", borderRadius: "50%" }} onClick={()=>{startMailSync(params.row.id,"full")}} >
                            <FontAwesomeIcon icon={faPlay} color={Colors.def_green} size="2x" />
                          </Button>;      
                }else{
                  return <div></div>;
                }
              }
        },
      ];

    const startMailSync = (id,type) => {
      let typeParam = (type == "full") ? "&full=true" : "&daily=true";
      let url = SftGlobalData.baseURL_MAIL_API+"sync?integrationId="+id + typeParam + "&force=true"; 
      axios.get(url, {
        responseType: 'text',
        headers: {
          'Accept': 'text/html'
        }
      })
      .then(response => {
        if(SftGlobalData.debug){
          console.log('url', url);
          console.log('response', response);
        }
        
        // Open new tab and write the HTML directly from the response
        const newTab = window.open('', '_blank');
        newTab.document.write(response.data); // This is the HTML from your backend
        newTab.document.close();
        
        // Show notification in current page
        const parser = new DOMParser();
        const htmlDoc = parser.parseFromString(response.data, 'text/html');
        const message = htmlDoc.querySelector('.success h2')?.textContent || "Sync started";
        props.notify(message, 7500, "top-right", props.theme, 1);
      })
      .catch(error => {
          if(SftGlobalData.debug){
              console.log('url', url);
              console.log('error', error);
          }
          props.notify(props.t('errorOccured'), 7500, "top-right", props.theme, 1);
      });
    }

    let navigate = useNavigate();


    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
      };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleChangeDense = (event) => {
        setDense(event.target.checked);
    };

    const reload = () => {
      if (props.api !== "") {
        props.changeScreen('Licence');
        SftGlobalData.post('mailIntegration&action=fetch', { key: props.api }).then(response => {
            setLists(response.data);
            SftGlobalData.post('mailIntegration&action=getSyncLog', { key: props.api }).then(response => {
              setLogRows(response.success ? response.data ?? [] : []);
              SftGlobalData.post('sftData', { key: props.api , type: 'mailLogWebhook' }).then(response => {
                if(response){
                    setLogRowsWebhook(response.success ? response.data?.data ?? [] : []);
                }
               setLoading(false);
            });
          });
        });
        setLoading(false);
      }
    }

    useEffect(() => {
        reload();
    },[props.api]);

    const changeMailCredentials = (list, active) => {
        SftGlobalData.post('mailIntegration&action=updateActive', { key: props.api, id: list, active: active === 'Y' ? '1' : '0' }).then(response => {
            setLoading(true);
            reload();
        });
    }

    return (
        <div className={(props.screenWidth) < 600 ? 'not_expanded container_content _80perc ' + props.theme + ' container_content_' + props.theme : 'expanded container_content _80perc ' + props.theme + ' container_content_' + props.theme}>
            
            <FloatingAddButton disabled={loading} onClick={()=>{navigate("/"+props.lang+"/mail-integrations/+")}} />
            <div className={"container_list__title " + props.theme}><h2>Mail integration</h2></div>

            <div style={{marginTop: 30}}>
                <br/><br/>
            {<Box sx={{ width: '100%' }} className={props.theme}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider'}} className={props.theme + ' container_tabs_below_subtitle'}>
                  <Tabs value={value} onChange={handleChange} className={props.theme}>
                        <Tab label="Lists" {...a11yProps(0)} className={props.theme} />
                        <Tab label="Sync log" {...a11yProps(1)} className={props.theme} />
                        {lists.filter(x => x.type === 'MailChimp').length > 0 && (
                            <Tab label="Webhook MailChimp" {...a11yProps(2)} className={props.theme} />
                        )}
                  </Tabs>
                </Box>
                <TabPanel value={value} index={0}>
                    <div>
                        <DataGridPro showToolbar
                        className={props.theme}
                        columns={gridIntegrationColumns}
                        rows={lists.map((row, index) => {return {
                                    id: row.id, 
                                    type: row.type,
                                    description: row.description,
                                    list_id: row.list_id,
                                    last_sync_started: row.last_sync_started,
                                    last_sync_end: row.last_sync_ended,
                                    active: (row.active == "1"),
                                    edit: "EDIT",
                                    daily: "DAILY",
                                    full: "FULL"
                                }
                            })}
                          loading={loading}
                          rowHeight={38}
                          autoHeight={true}
                          pagination={true}
                        />
                    </div>
                </TabPanel>
                <TabPanel value={value} index={1}>
                    {logRows.length !== 0 && <div>
                        <Paper sx={{ width: '100%', mb: 2 }}>
                            <TableContainer component={Paper} className={props.theme}>
                                {/* tableLayout: 'fixed' is the whole fix. With the default 'auto' the
                                    widest cell decides the table width, and the expanded detail row —
                                    which holds grids whose columns are built dynamically from the data —
                                    stretched the entire log far past the screen. The only horizontal
                                    scrollbar then sat at the very bottom of an already tall table.
                                    Fixed layout pins the width to the container, so the detail scrolls
                                    inside itself (see the Box in Row) and the log always fits. */}
                                <Table aria-label="collapsible table" className={props.theme} size={dense ? 'small' : 'medium'} sx={{ tableLayout: 'fixed', width: '100%' }}>
                                        <EnhancedTableHead
                                            theme={props.theme}
                                            order={order}
                                            orderBy={orderBy}
                                            onRequestSort={handleRequestSort}
                                            rowCount={logRows.length}
                                        />
                                    <TableBody>

                                    {stableSort(logRows, getComparator(order, orderBy))
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((row, index) => {
                                        //const labelId = `enhanced-table-checkbox-${index}`;

                                        return (
                                            <Row key={row.date + " " + row.start_time} theme={props.theme} row={row} api={props.api} tabIndex={-1}/>
                                        )
                                    })}

                                    {/* {emptyRows > 0 && (
                                        <TableRow
                                        style={{
                                            height: (dense ? 33 : 53) * emptyRows,
                                        }}
                                        >
                                            <TableCell colSpan={8} />
                                        </TableRow>
                                    )} */}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                className={props.theme}
                                rowsPerPageOptions={[5, 10, 25]}
                                component="div"
                                count={logRows.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                />
                        </Paper>
                        <FormControlLabel
                            control={<Switch checked={dense} onChange={handleChangeDense} />}
                            label="Dense padding"
                        />
                    </div>}
                    {logRows.length === 0 && <div>
                        <center><p>No mail sync logging for client.</p></center>
                    </div>}
                </TabPanel>
                <TabPanel value={value} index={2}>
                  <div style={{width: '100%' }}>
                    <DataGridPro showToolbar
                    className={props.theme}
                    columns={gridWebhookColumns}
                    rows={logRowsWebhook.map((row, index) => {return {
                                id: row.rID, 
                                rTime: SftGlobalData.dateTimeFormatter(row.rTime),
                                rType: row.rType,
                                rListID: row.rListID,
                                rSubID: row.rSubID,
                                rEmail: row.rEmail,
                                rFree1: row.rFree1,
                                rFree2: row.rFree2
                            }
                        })}
                      loading={loading}
                      rowHeight={38}
                      pagination={true}
                      autoHeight={true}
                    />
                  </div>
                </TabPanel>
            </Box>}
            </div>        
        </div>
    );
}

export default MailIntegrationsScreen;
